import React, { useState, useEffect } from 'react';
import { Grid, Box, Card, CardContent, Typography, CircularProgress } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ChatIcon from '@mui/icons-material/Chat';
import { toast } from 'react-toastify';
import StatsCard from '../components/StatsCard';
import RevenueTrend from '../components/charts/RevenueTrend';
import UserGrowth from '../components/charts/UserGrowth';
import TopAstrologers from '../components/charts/TopAstrologers';
import ServiceBreakdown from '../components/charts/ServiceBreakdown';
import { getDashboardStats } from '../api/endpoints/dashboard';
import { getRevenueReport } from '../api/endpoints/reports';
import { formatCurrency } from '../utils/formatters';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [topAstrologers, setTopAstrologers] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const [dashRes, reportRes] = await Promise.allSettled([
        getDashboardStats(),
        getRevenueReport(),
      ]);
      if (dashRes.status === 'fulfilled') {
        const data = dashRes.value.data.data;
        setStats(data.stats);
        setCharts(data.charts);
        setTopAstrologers(data.topAstrologers || []);
      } else {
        toast.error('Failed to load dashboard data');
      }
      if (reportRes.status === 'fulfilled') {
        setReportData(reportRes.value.data.data);
      }
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>Dashboard</Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard icon={PeopleIcon} label="Total Users" value={stats?.totalUsers?.toLocaleString() || '0'} color="#2D1B69" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard icon={AutoAwesomeIcon} label="Total Astrologers" value={stats?.totalAstrologers?.toLocaleString() || '0'} color="#7C5CBF" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard icon={AccountBalanceWalletIcon} label="Total Revenue" value={formatCurrency(stats?.totalRevenue || 0)} color="#FFD700" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard icon={ChatIcon} label="Active Chats" value={stats?.activeChats?.toLocaleString() || '0'} color="#4CAF50" />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Revenue Trend</Typography>
              {charts ? <RevenueTrend data={charts.transactions || []} /> : <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Service Breakdown</Typography>
              {reportData ? <ServiceBreakdown data={reportData.byCategory || []} /> : <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>User Growth</Typography>
              {charts ? <UserGrowth data={charts.userGrowth || []} /> : <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Top Astrologers</Typography>
              {topAstrologers.length > 0 ? <TopAstrologers data={topAstrologers} /> : <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;